import { RadioGroup } from '@mui/material';

import { FormLabel, LabeledRadio } from '../Forms';
import type { SelectOption } from '../../../types';

export type RadioFilterOption = {
  label: string;
  value: string;
};

type Props<T = string> = {
  //  Label for the multiselect.
  label: string;
  //  This is all of the checkboxes, with `label` used as the label and `value` as the key. Note you can use `selectOptionFactory` to create SelectOptions easily.
  options: Array<SelectOption<T>>;
  //  Value is the value which is currently selected.
  value: SelectOption<T>['value'];
  //  Change handler. Gets all selected options after the change takes effect.
  onChange: (value: SelectOption<T>['value']) => void;
  //  If the checkboxes are disabled or not.
  isDisabled?: boolean;
  //  ID for testing purposes.
  testId?: string;
};

/**
 * FilterCardRadios renders a label with a set of radios.
 * They use the SelectOption shape, where `label` is the text label
 * rendered beside the checkbox, and `value` is a unique key for that radio.
 *
 * This is intended to be used as a child of FilterCard, however it could
 * probably also be used outside that context.
 */
const FilterCardRadios = <T = string>({
  label,
  options,
  value,
  onChange,
  isDisabled,
  testId,
}: Props<T>) => (
  <RadioGroup
    value={value}
    onChange={(_event, val) => onChange(val as T)}
    name={label}
    data-testid={testId}
  >
    <FormLabel
      label={label}
    />
    {options.map(option =>
      <LabeledRadio
        label={option.label}
        value={option.value as string}
        isDisabled={isDisabled}
        isRippleDisabled
      />)}
  </RadioGroup>);

export default FilterCardRadios;
